import React from 'react';
import { Box, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Timestamp } from 'firebase/firestore';
import { toSafeDate } from '../../utils/appDateFunctions';

interface DateRangeValue {
  start: Timestamp | null;
  end: Timestamp | null;
}

interface DateRangeFieldProps {
  value: DateRangeValue;
  onChange: (value: DateRangeValue) => void;
  disabled?: boolean;
}

const DateRangeField: React.FC<DateRangeFieldProps> = ({ value, onChange, disabled }) => {
  const startValue = toSafeDate(value?.start);
  const endValue = toSafeDate(value?.end);

  const handleStartChange = (newValue: Date | null) => {
    onChange({
      ...value,
      start: newValue && !isNaN(newValue.getTime()) ? Timestamp.fromDate(newValue) : null,
    });
  };

  const handleEndChange = (newValue: Date | null) => {
    onChange({
      ...value,
      end: newValue && !isNaN(newValue.getTime()) ? Timestamp.fromDate(newValue) : null,
    });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'center',
          justifyContent: 'center',
          gap: { xs: 2, sm: 3 },
          mt: 2,
        }}
      >
        <DatePicker
          value={startValue}
          onChange={handleStartChange}
          disabled={disabled}
          slotProps={{
            textField: {
              variant: 'standard',
              placeholder: 'Start date',
              sx: {
                minWidth: 160,
                '& .MuiInput-root': {
                  fontSize: '1.125rem',
                },
                '& .MuiInput-input': {
                  textAlign: 'center',
                  py: 1,
                },
              },
            },
          }}
        />
        <Typography color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
          to
        </Typography>
        <DatePicker
          value={endValue}
          onChange={handleEndChange}
          disabled={disabled}
          minDate={startValue || undefined}
          slotProps={{
            textField: {
              variant: 'standard',
              placeholder: 'End date',
              sx: {
                minWidth: 160,
                '& .MuiInput-root': {
                  fontSize: '1.125rem',
                },
                '& .MuiInput-input': {
                  textAlign: 'center',
                  py: 1,
                },
              },
            },
          }}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default DateRangeField;
